import type { StationeryDesignGalleryFilterTagsFragment } from '../../DesignsGallery.graphql.generated';
import { COLOR_FACET, STYLE_FACET } from './Filters.constants';
import type { Facet } from './Filter.types';

const dynamicFacetOptionStrings = new Map<string, string>();
const dynamicFacetOptionStringToLabel = new Map<string, string>();
const colorFacetOptions = new Set<string>();
const styleFacetOptions = new Set<string>();
const STYLE_FACET_OPTION_BY_CONCEPT_NAME: ReadonlyMap<string, string> = new Map([
  ['botanical leaves', `${STYLE_FACET}:botanical`],
  ['minimal simple', `${STYLE_FACET}:minimal`],
]);

const CATEGORY_TO_FACET: Partial<Record<string, Facet>> = {
  Colors: COLOR_FACET,
  Styles: STYLE_FACET,
};

export function prepareFacetOptions(tagDefs: StationeryDesignGalleryFilterTagsFragment[] = []) {
  for (const tagDef of tagDefs) {
    const { category, name, displayLabel } = tagDef;
    const facet = CATEGORY_TO_FACET[category ?? ''];
    if (!facet) continue;
    dynamicFacetOptionStrings.set(name, `${facet}:${name}`);
    dynamicFacetOptionStringToLabel.set(`${facet}:${name}`, displayLabel);
    if (facet === COLOR_FACET) colorFacetOptions.add(`${facet}:${name}`);
    if (facet === STYLE_FACET) styleFacetOptions.add(`${facet}:${name}`);
  }
}

/**
 * The `color:<tagName>` facet option for a color name classified by the
 * theme-search embeddings index, or `undefined` when no such tag exists — the
 * filter then has no option to match, so that color is not selectable and the
 * colorway simply doesn't participate (the index also classifies some rows as
 * `colorful`, which is deliberately not a color option).
 *
 * Names are compared verbatim: the `Colors` tags and the index's palette are both
 * maintained in-house off one vocabulary, so a spelling variant would be a bug to
 * fix at the source rather than absorb here.
 */
export function colorFacetOptionForColorName(colorName: string) {
  const facetOption = `${COLOR_FACET}:${colorName}`;
  return colorFacetOptions.has(facetOption) ? facetOption : undefined;
}

/** Configured Styles option for an embeddings-index concept, including the known botanical alias. */
export function styleFacetOptionForConceptName(conceptName: string) {
  const facetOption = STYLE_FACET_OPTION_BY_CONCEPT_NAME.get(conceptName) ?? `${STYLE_FACET}:${conceptName}`;
  return styleFacetOptions.has(facetOption) ? facetOption : undefined;
}

export function mapToFacetOption(tags: string[]) {
  return tags.filter(tag => dynamicFacetOptionStrings.has(tag)).map(tag => dynamicFacetOptionStrings.get(tag)!);
}

export function mapToLabel(facetOption: string) {
  return dynamicFacetOptionStringToLabel.get(facetOption);
}
